import type { HiveTask } from "@/types";

/**
 * Incoming work, normalised across Jira / GitHub / Slack / email / manual into
 * a single queue. Vague tickets carry low clarity scores and open questions;
 * refined tickets carry acceptance criteria, a plan and a verification plan so
 * ARES can pick them up.
 */
export const HIVE_TASKS: HiveTask[] = [
  {
    id: "PAY-431",
    title: "Handle failed payment retries",
    source: "jira",
    priority: "high",
    status: "needs-clarification",
    assignee: "Priya N.",
    reporter: "Marco S.",
    workspaceId: "ws-checkout",
    repo: "payments-api",
    created: "2h ago",
    sla: "Due in 1d",
    autoResolvable: false,
    refined: false,
    scores: { clarity: 38, scope: 52, readiness: 41, verification: 30, risk: 72 },
    description:
      "When a payment fails we should retry it. Right now nothing happens and customers get stuck. Please make the retries work properly.",
    refinedDescription:
      "Implement an idempotent retry policy for failed payment intents in payments-api. Failed charges from supported providers (Stripe, Adyen) should be retried with exponential backoff (max 3 attempts, jittered) behind a single idempotency key so a retry never double-charges. Surface terminal failures to the customer with a recoverable error state; log transient failures internally with structured context for observability.",
    acceptance: [
      "A failed PaymentIntent is retried up to 3× with jittered exponential backoff",
      "Retries reuse one idempotency key — no double charges under retry storms",
      "Terminal failures return a customer-visible recoverable error",
      "Transient failures are logged with provider, attempt and correlation id",
    ],
    technical: [
      "Add RetryPolicy to PaymentGateway adapter interface",
      "Persist attempt count + idempotency key on PaymentIntent",
      "Emit `payment.retry.*` events to Datadog",
    ],
    files: ["src/payments/gateway.ts", "src/payments/intent.ts", "src/observability/events.ts"],
    dependencies: ["Datadog event schema (INT-Datadog)", "PaymentIntent migration"],
    questions: [
      { id: "q1", text: "Which payment providers should this support — Stripe only, or Adyen too?", status: "open" },
      { id: "q2", text: "Should failed payment retries be visible to the customer or only logged internally?", status: "open" },
      { id: "q3", text: "What is the expected behaviour for failed retries after the final attempt?", status: "open" },
    ],
    plan: [
      "Define RetryPolicy + backoff strategy",
      "Thread idempotency key through gateway calls",
      "Add migration for attempt tracking",
      "Wire customer-facing recoverable error state",
      "Emit retry telemetry",
    ],
    verificationPlan: [
      "Property test: N concurrent retries never double-charge",
      "Regression: retry-storm load test",
      "Contract: PaymentIntent schema unchanged for clients",
    ],
    edgeCases: [
      "Provider returns success on a retry after a network timeout",
      "Idempotency key collision across two intents",
      "Customer cancels mid-retry",
    ],
    risk: "Touches money movement — incorrect idempotency could double-charge customers. Requires human approval before ARES execution.",
    suggestedOwner: "Priya N. (payments-core owner)",
  },
  {
    id: "PAY-440",
    title: "Add idempotency keys to checkout intent",
    source: "jira",
    priority: "high",
    status: "ready",
    assignee: "Priya N.",
    reporter: "Priya N.",
    workspaceId: "ws-checkout",
    repo: "payments-api",
    created: "1d ago",
    sla: "Due in 3d",
    autoResolvable: true,
    refined: true,
    scores: { clarity: 92, scope: 88, readiness: 94, verification: 90, risk: 28 },
    description:
      "Checkout intent creation is not idempotent; duplicate POSTs create duplicate intents.",
    refinedDescription:
      "Make POST /checkout/intent idempotent by accepting an Idempotency-Key header. Identical keys within a 24h window must return the original intent rather than creating a new one. Keys are scoped per merchant.",
    acceptance: [
      "Identical Idempotency-Key returns the original intent (200, not 201)",
      "Keys expire after 24h",
      "Keys are scoped per merchant id",
    ],
    technical: ["Add idempotency_keys table", "Middleware to short-circuit duplicates"],
    files: ["src/checkout/intent.ts", "src/middleware/idempotency.ts"],
    dependencies: [],
    questions: [],
    plan: ["Add table + index", "Add middleware", "Backfill docs"],
    verificationPlan: ["Property test for duplicate suppression", "Contract test on header"],
    edgeCases: ["Key reused with a different body → 409"],
    risk: "Low — additive, well-scoped, behind a header.",
    suggestedOwner: "Priya N.",
  },
  {
    id: "GH-2204",
    title: "Native Apple Pay sheet a11y failures",
    source: "github",
    priority: "medium",
    status: "blocked",
    assignee: "Dana K.",
    reporter: "a11y-bot",
    workspaceId: "ws-mobile",
    repo: "mobile-checkout",
    created: "1d ago",
    sla: "No SLA",
    autoResolvable: false,
    refined: false,
    scores: { clarity: 64, scope: 70, readiness: 44, verification: 58, risk: 40 },
    description:
      "VoiceOver does not announce the Apple Pay sheet total. Accessibility audit failing on the payment sheet.",
    refinedDescription:
      "Restore VoiceOver support on the native Apple Pay sheet: the sheet total, line items and the pay button must be announced with correct accessibility labels and traits. Blocked on the design-system sheet component shipping its a11y props.",
    acceptance: [
      "VoiceOver announces total, line items and pay button",
      "Audit suite passes on payment sheet",
    ],
    technical: ["Add accessibilityLabel to PaySheetView", "Await design-system a11y props"],
    files: ["Sources/Checkout/PaySheetView.swift"],
    dependencies: ["design-system PR #812 (blocked)"],
    questions: [
      { id: "q4", text: "Is the design-system a11y prop API frozen, or still changing?", status: "open" },
    ],
    plan: ["Unblock on design-system", "Add labels", "Run audit"],
    verificationPlan: ["Accessibility snapshot suite"],
    edgeCases: ["Dynamic type at largest size clips total"],
    risk: "Blocked by an upstream PR — cannot start until #812 merges.",
    suggestedOwner: "Dana K.",
  },
  {
    id: "RDY-118",
    title: "Generate MCP manifest for billing-svc",
    source: "jira",
    priority: "medium",
    status: "ready",
    assignee: "Sven O.",
    reporter: "Sven O.",
    workspaceId: "ws-readiness",
    repo: "ai-readiness-service",
    created: "3h ago",
    sla: "Due in 2d",
    autoResolvable: true,
    refined: true,
    scores: { clarity: 90, scope: 86, readiness: 91, verification: 88, risk: 22 },
    description: "We need an MCP manifest exported for billing-svc so agents can call it.",
    refinedDescription:
      "Export a valid MCP server manifest for billing-svc covering all 38 endpoints, derived from the existing OpenAPI spec. Manifest must validate against the MCP schema and refresh on schema drift.",
    acceptance: ["Manifest covers all 38 endpoints", "Validates against MCP schema", "Auto-refreshes on drift"],
    technical: ["Reuse OpenAPI→MCP exporter", "Hook into drift detector"],
    files: ["src/mcp/exporter.py", "src/drift/detector.py"],
    dependencies: [],
    questions: [],
    plan: ["Map OpenAPI ops to MCP tools", "Validate", "Wire drift hook"],
    verificationPlan: ["Schema validation in CI", "Drift refresh test"],
    edgeCases: ["Endpoint with no response schema"],
    risk: "Low — derived artifact, no runtime behaviour change.",
    suggestedOwner: "ATHENA agent",
  },
  {
    id: "SEC-77",
    title: "Revoke sessions on password reset",
    source: "jira",
    priority: "urgent",
    status: "in-progress",
    assignee: "Aria L.",
    reporter: "Security Guild",
    workspaceId: "ws-portal",
    repo: "customer-portal",
    created: "5h ago",
    sla: "Due in 8h",
    autoResolvable: false,
    refined: true,
    scores: { clarity: 88, scope: 82, readiness: 79, verification: 84, risk: 66 },
    description: "Resetting a password should invalidate all other active sessions.",
    refinedDescription:
      "On a successful password reset, revoke every other active session for the user (refresh + access tokens) and force re-authentication. Current session may persist. Emit a security event.",
    acceptance: [
      "All other sessions are revoked on reset",
      "Current session remains valid",
      "Security event emitted to Sentry + audit log",
    ],
    technical: ["Bump token generation per user", "Invalidate refresh token family"],
    files: ["src/auth/session.ts", "src/auth/reset.ts"],
    dependencies: ["Merge conflict in auth/session.ts"],
    questions: [
      { id: "q5", text: "Should the initiating session also be revoked, or kept alive?", status: "resolved" },
    ],
    plan: ["Resolve merge conflict", "Bump token gen", "Emit event", "Verify"],
    verificationPlan: ["Proof: no revoked token authenticates", "CSRF regression"],
    edgeCases: ["Concurrent reset from two devices"],
    risk: "Security-sensitive; high urgency. Workspace currently has a merge conflict.",
    suggestedOwner: "Aria L.",
  },
  {
    id: "SLK-12",
    title: "Dashboard is slow after the 4.1 release",
    source: "slack",
    priority: "medium",
    status: "needs-refinement",
    assignee: "—",
    reporter: "@daniel",
    workspaceId: "ws-portal",
    repo: "customer-portal",
    created: "40m ago",
    sla: "No SLA",
    autoResolvable: false,
    refined: false,
    scores: { clarity: 22, scope: 30, readiness: 18, verification: 20, risk: 48 },
    description:
      "hey the dashboard feels really sluggish since we shipped 4.1 — a few customers complained. can someone look?",
    refinedDescription:
      "Investigate dashboard render regression introduced in 4.1. Establish a reproducible scenario and a p95 latency budget, profile the slow path, and propose a fix. Likely candidates: N+1 in the activity feed query and unmemoised chart re-renders.",
    acceptance: [
      "Reproducible slow scenario with a metric",
      "p95 dashboard load under target after fix",
      "Root cause documented",
    ],
    technical: ["Profile activity feed query", "Audit chart memoisation"],
    files: ["src/dashboard/Feed.tsx", "src/dashboard/Charts.tsx"],
    dependencies: [],
    questions: [
      { id: "q6", text: "Which customers / accounts reported this — can we get a tenant id to reproduce?", status: "open" },
      { id: "q7", text: "What does 'slow' mean here — initial load, navigation, or chart interaction?", status: "open" },
    ],
    plan: ["Reproduce", "Profile", "Fix", "Verify budget"],
    verificationPlan: ["Performance budget test in CI"],
    edgeCases: ["Large tenants with 10k+ activity rows"],
    risk: "Vague report — needs clarification before scoping.",
    suggestedOwner: "Aria L.",
  },
  {
    id: "ROUTE-9",
    title: "Router panics on empty cost policy",
    source: "github",
    priority: "urgent",
    status: "assigned",
    assignee: "Router-Prime (ARES)",
    reporter: "Nora P.",
    workspaceId: "ws-routing",
    repo: "model-routing-gateway",
    created: "1h ago",
    sla: "Due in 4h",
    autoResolvable: false,
    refined: true,
    scores: { clarity: 86, scope: 80, readiness: 88, verification: 82, risk: 58 },
    description: "Gateway panics when the cost policy config is empty instead of falling back to defaults.",
    refinedDescription:
      "Guard the router against an empty or missing cost policy by falling back to the built-in default policy instead of panicking. Add a startup validation warning when the policy is empty.",
    acceptance: ["Empty policy → default policy, no panic", "Startup warning logged"],
    technical: ["Add Option handling in policy loader", "Default policy constant"],
    files: ["src/router/policy.rs", "src/router/mod.rs"],
    dependencies: [],
    questions: [],
    plan: ["Handle None policy", "Add default", "Log warning", "Test"],
    verificationPlan: ["Fuzz: empty/partial policies never panic"],
    edgeCases: ["Partially-specified policy"],
    risk: "Urgent crash fix; already assigned to ARES.",
    suggestedOwner: "Router-Prime (ARES)",
  },
  {
    id: "INFRA-301",
    title: "Add cost-policy guardrails to node pools",
    source: "manual",
    priority: "low",
    status: "new",
    assignee: "—",
    reporter: "Tom B.",
    workspaceId: "ws-infra",
    repo: "infra-orchestrator",
    created: "1d ago",
    sla: "No SLA",
    autoResolvable: true,
    refined: false,
    scores: { clarity: 58, scope: 62, readiness: 50, verification: 55, risk: 34 },
    description: "We should cap how much each region can spend on node pools so we don't blow the budget.",
    refinedDescription:
      "Introduce per-region spend guardrails for node pools. Each region gets a monthly budget; scaling decisions that would exceed it require approval or are capped. Expose current spend vs budget as a metric.",
    acceptance: ["Per-region budget enforced", "Spend-vs-budget metric exported", "Over-budget scale needs approval"],
    technical: ["Budget config per region", "Hook into scaling pilot"],
    files: ["pkg/scaling/policy.go", "pkg/budget/budget.go"],
    dependencies: [],
    questions: [{ id: "q8", text: "Hard cap or soft cap with approval override?", status: "open" }],
    plan: ["Add budget config", "Enforce in scaler", "Export metric"],
    verificationPlan: ["Unit: over-budget scale blocked"],
    edgeCases: ["Mid-month budget change"],
    risk: "Low — additive guardrail behind config.",
    suggestedOwner: "Tom B.",
  },
  {
    id: "GH-2280",
    title: "Bump checkout SDK to 4.1",
    source: "github",
    priority: "low",
    status: "done",
    assignee: "Lee W.",
    reporter: "dependabot",
    workspaceId: "ws-mobile",
    repo: "mobile-checkout",
    created: "3d ago",
    sla: "—",
    autoResolvable: true,
    refined: true,
    scores: { clarity: 95, scope: 92, readiness: 100, verification: 96, risk: 12 },
    description: "Upgrade checkout SDK from 4.0 to 4.1.",
    refinedDescription: "Bump checkout SDK to 4.1, update changed call sites, run the migration codemod.",
    acceptance: ["SDK at 4.1", "Build green", "Migration codemod applied"],
    technical: ["Run codemod", "Fix breaking call sites"],
    files: ["Package.swift"],
    dependencies: [],
    questions: [],
    plan: ["Bump", "Codemod", "Verify"],
    verificationPlan: ["Build + snapshot suite"],
    edgeCases: [],
    risk: "Done.",
    suggestedOwner: "Lee W.",
  },
  {
    id: "EMAIL-58",
    title: "Customer requests SSO via Okta",
    source: "email",
    priority: "medium",
    status: "needs-refinement",
    assignee: "—",
    reporter: "enterprise@acme.com",
    workspaceId: "ws-portal",
    repo: "customer-portal",
    created: "6h ago",
    sla: "Due in 5d",
    autoResolvable: false,
    refined: false,
    scores: { clarity: 34, scope: 40, readiness: 26, verification: 36, risk: 56 },
    description:
      "Forwarded from sales: 'Acme needs to log in with their Okta. They use SAML. This is a deal blocker for renewal.'",
    refinedDescription:
      "Add SAML 2.0 SSO support with Okta as the first IdP for enterprise tenants. Per-tenant IdP config, JIT user provisioning, and a fallback to password login for non-SSO tenants.",
    acceptance: ["SAML login via Okta works for a test tenant", "JIT provisioning", "Per-tenant config"],
    technical: ["Add SAML SP", "Per-tenant IdP metadata store"],
    files: ["src/auth/saml.ts", "src/tenants/idp.ts"],
    dependencies: ["SSO / Identity Provider integration (admin-managed)"],
    questions: [
      { id: "q9", text: "Which IdPs beyond Okta must we support in v1 (Azure AD, Google)?", status: "open" },
      { id: "q10", text: "Is JIT provisioning required, or are users pre-provisioned?", status: "open" },
    ],
    plan: ["Stand up SAML SP", "Tenant IdP config", "JIT provisioning", "Fallback"],
    verificationPlan: ["E2E SAML round-trip against Okta sandbox"],
    edgeCases: ["User exists with password + later joins SSO tenant"],
    risk: "Depends on the admin-managed SSO integration; needs admin enablement.",
    suggestedOwner: "Aria L.",
  },
  {
    id: "RDY-120",
    title: "Refresh llms.txt nightly",
    source: "manual",
    priority: "low",
    status: "new",
    assignee: "—",
    reporter: "Sven O.",
    workspaceId: "ws-readiness",
    repo: "ai-readiness-service",
    created: "8h ago",
    sla: "No SLA",
    autoResolvable: true,
    refined: false,
    scores: { clarity: 70, scope: 74, readiness: 62, verification: 66, risk: 18 },
    description: "Make llms.txt regenerate on a nightly schedule instead of manually.",
    refinedDescription:
      "Schedule a nightly job that regenerates llms.txt for all registered services and opens a PR only when the content changes.",
    acceptance: ["Nightly regeneration", "PR opened only on diff", "Failures alert Slack"],
    technical: ["Cron hook", "Diff-gate PR creation"],
    files: ["src/jobs/llms_refresh.py"],
    dependencies: [],
    questions: [],
    plan: ["Add cron", "Diff gate", "Alert on fail"],
    verificationPlan: ["Dry-run job in CI"],
    edgeCases: ["No services changed → no PR"],
    risk: "Low — scheduled, idempotent, behind a diff gate.",
    suggestedOwner: "ATHENA agent",
  },
  {
    id: "SLK-19",
    title: "Can we add dark mode to the portal?",
    source: "slack",
    priority: "low",
    status: "new",
    assignee: "—",
    reporter: "@megan",
    workspaceId: "ws-portal",
    repo: "customer-portal",
    created: "20m ago",
    sla: "No SLA",
    autoResolvable: false,
    refined: false,
    scores: { clarity: 28, scope: 44, readiness: 22, verification: 30, risk: 26 },
    description: "a couple customers asked for dark mode 🌙 is that on the roadmap?",
    refinedDescription:
      "Add a dark theme to the customer portal with a system / light / dark toggle, persisted per user, using existing design tokens.",
    acceptance: ["System/light/dark toggle", "Persisted per user", "All surfaces themed"],
    technical: ["Add theme tokens", "Persist preference"],
    files: ["src/theme/tokens.ts", "src/theme/Provider.tsx"],
    dependencies: [],
    questions: [
      { id: "q11", text: "Should this follow the OS theme by default, or default to light?", status: "open" },
    ],
    plan: ["Token pass", "Toggle", "Persist"],
    verificationPlan: ["Visual regression across themes"],
    edgeCases: ["Charts with hard-coded colours"],
    risk: "Low — cosmetic, but scope is fuzzy until clarified.",
    suggestedOwner: "Dana K.",
  },
];

export const TASK_SOURCE_LABEL: Record<HiveTask["source"], string> = {
  jira: "Jira",
  github: "GitHub",
  slack: "Slack",
  manual: "Manual",
  email: "Email",
};

export const TASK_STATUS_LABEL: Record<HiveTask["status"], string> = {
  new: "New",
  "needs-refinement": "Needs Refinement",
  "needs-clarification": "Needs Clarification",
  ready: "Ready for ARES",
  assigned: "Assigned to ARES",
  "in-progress": "In Progress",
  blocked: "Blocked",
  done: "Done",
};

/** Status label that only name-drops ARES when the tenant actually has it. */
export function taskStatusLabel(status: HiveTask["status"], aresEnabled: boolean): string {
  if (!aresEnabled && status === "ready") return "Ready";
  if (!aresEnabled && status === "assigned") return "Assigned";
  return TASK_STATUS_LABEL[status];
}

export const TASK_STATUS_TONE: Record<HiveTask["status"], string> = {
  new: "queued",
  "needs-refinement": "syncing",
  "needs-clarification": "degraded",
  ready: "connected",
  assigned: "internal",
  "in-progress": "running",
  blocked: "error",
  done: "done",
};

export const PRIORITY_TONE: Record<HiveTask["priority"], string> = {
  low: "queued",
  medium: "syncing",
  high: "degraded",
  urgent: "error",
};

export const AUTO_RESOLVE_LEVELS = [
  {
    level: 0,
    name: "Manual Only",
    short: "Manual",
    desc: "Hive only suggests refinements. Nothing is changed without you.",
  },
  {
    level: 1,
    name: "Draft Refinements",
    short: "Draft",
    desc: "Hive drafts refined tickets but waits for your approval.",
  },
  {
    level: 2,
    name: "Auto-Refine Safe Tasks",
    short: "Auto-refine",
    desc: "Hive automatically refines low-risk tasks. Risky tickets still wait.",
  },
  {
    level: 3,
    name: "Auto-Ask Follow-Ups",
    short: "Auto-ask",
    desc: "Hive refines and sends clarification questions to assignees / reporters.",
  },
  {
    level: 4,
    name: "Full Queue Assist",
    short: "Full assist",
    desc: "Hive refines, asks questions, marks readiness, and prepares tasks for ARES.",
  },
];

export const AUTO_RESOLVE_GUARDRAILS = [
  "Requires admin approval for external posting",
  "Slack messages are logged",
  "High-risk tickets will still require human approval",
  "ARES execution will not begin without workspace confirmation",
];
